import { useState, useEffect } from 'react'
import { Calendar, Plus, Edit, Trash2, Eye, AlertCircle, Loader2, Megaphone } from 'lucide-react'
import { format } from 'date-fns'
import { eventsAPI, adminAPI } from '@/lib/api'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import PageHeader from '@/components/PageHeader'
import { useAuth } from '@/hooks/useAuth'

/* Broadcast-blokje: 1 titel + optioneel bericht + link → gaat naar
   POST /notifications/admin/broadcast → iedereen krijgt toast + bell-rij. */
function BroadcastCard() {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [actionUrl, setActionUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState(null)

  const verzend = async () => {
    if (!title.trim()) { setFeedback({ type: 'err', text: 'Titel is verplicht' }); return }
    if (!window.confirm(`"${title.trim()}" naar alle users sturen?`)) return
    setBusy(true); setFeedback(null)
    try {
      const r = await adminAPI.broadcastNotification({
        title: title.trim(),
        message: message.trim() || null,
        action_url: actionUrl.trim() || null,
      })
      setFeedback({ type: 'ok', text: `Verstuurd naar ${r.data.count} users` })
      setTitle(''); setMessage(''); setActionUrl('')
    } catch (e) {
      setFeedback({ type: 'err', text: e.response?.data?.error || e.message })
    } finally { setBusy(false) }
  }

  return (
    <Card className="border-neon-purple/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-neon-purple" />
          Broadcast-notificatie
        </CardTitle>
        <CardDescription className="text-xs">
          Iedereen krijgt onmiddellijk een toast + een rij in z'n notificatie-bel.
          Voor blijvende aankondigingen op de home: gebruik de "Announcement"-tab hieronder.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
          <div>
            <Label htmlFor="bc-title" className="text-xs">Titel *</Label>
            <Input id="bc-title" value={title} onChange={e => setTitle(e.target.value)}
                   placeholder="bv. Prijzenkraam gaat open om 22u!"
                   disabled={busy} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="bc-url" className="text-xs">Link (optioneel)</Label>
            <Input id="bc-url" value={actionUrl} onChange={e => setActionUrl(e.target.value)}
                   placeholder="bv. /prijzenkraam"
                   disabled={busy} className="mt-1" />
          </div>
        </div>
        <div>
          <Label htmlFor="bc-msg" className="text-xs">Bericht (optioneel)</Label>
          <Textarea id="bc-msg" value={message} onChange={e => setMessage(e.target.value)}
                    placeholder="Extra info…" rows={2}
                    disabled={busy} className="mt-1" />
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={verzend} disabled={busy || !title.trim()}
                  className="bg-neon-purple/10 hover:bg-neon-purple/20 text-neon-purple border border-neon-purple/30"
                  variant="outline">
            <Megaphone className="h-4 w-4 mr-1" />
            {busy ? 'Verzenden…' : 'Verzend naar iedereen'}
          </Button>
          {feedback && (
            <span className={`text-xs ${feedback.type === 'ok' ? 'text-live' : 'text-signal'}`}>
              {feedback.text}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export default function EventsPage() {
  const [pages, setPages] = useState([])
  const [filteredPages, setFilteredPages] = useState([])
  const [activeTab, setActiveTab] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedPage, setSelectedPage] = useState(null)

  // Form state for create/edit
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    page_type: 'info',
    display_order: 0,
    is_published: true,
  })

  // Echte auth-check — vroeger stond hier `setIsAdmin(!!token)` waardoor
  // elke ingelogde user de admin-UI zag (broadcast-form, create-page, etc.).
  const { isAdmin } = useAuth()

  useEffect(() => { fetchPages() }, [])

  useEffect(() => {
    filterPages()
  }, [pages, activeTab])

  const fetchPages = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await eventsAPI.getAllPages({ published_only: !isAdmin })
      setPages(response.data.pages || [])
    } catch (err) {
      console.error('Failed to fetch event pages:', err)
      setError('Failed to load event pages. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const filterPages = () => {
    if (activeTab === 'all') {
      setFilteredPages(pages)
    } else {
      setFilteredPages(pages.filter(page => page.page_type === activeTab))
    }
  }

  const handleViewPage = (page) => {
    setSelectedPage(page)
    setViewDialogOpen(true)
  }

  const handleEditPage = (page) => {
    setSelectedPage(page)
    setFormData({
      title: page.title,
      content: page.content,
      page_type: page.page_type,
      display_order: page.display_order,
      is_published: page.is_published === 1,
    })
    setEditDialogOpen(true)
  }

  const handleCreatePage = () => {
    setSelectedPage(null)
    setFormData({
      title: '',
      content: '',
      page_type: 'info',
      display_order: 0,
      is_published: true,
    })
    setEditDialogOpen(true)
  }

  const handleSavePage = async () => {
    try {
      const pageData = {
        ...formData,
        is_published: formData.is_published ? 1 : 0,
      }

      if (selectedPage) {
        // Update existing page
        await eventsAPI.updatePage(selectedPage.id, pageData)
      } else {
        // Create new page
        await eventsAPI.createPage(pageData)
      }

      setEditDialogOpen(false)
      fetchPages()
    } catch (err) {
      console.error('Failed to save page:', err)
      alert('Failed to save page. Please try again.')
    }
  }

  const handleDeletePage = async (pageId) => {
    if (!confirm('Are you sure you want to delete this page?')) {
      return
    }

    try {
      await eventsAPI.deletePage(pageId)
      fetchPages()
    } catch (err) {
      console.error('Failed to delete page:', err)
      alert('Failed to delete page. Please try again.')
    }
  }

  const getPageTypeBadgeColor = (pageType) => {
    switch (pageType) {
      case 'info':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/30 hover:bg-blue-500/20'
      case 'rules':
        return 'bg-pink-500/10 text-pink-500 border-pink-500/30 hover:bg-pink-500/20'
      case 'schedule':
        return 'bg-green-500/10 text-green-500 border-green-500/30 hover:bg-green-500/20'
      case 'announcement':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/30 hover:bg-purple-500/20'
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/30'
    }
  }

  const truncateContent = (content, lines = 3) => {
    const contentLines = content.split('\n')
    if (contentLines.length <= lines) {
      return content
    }
    return contentLines.slice(0, lines).join('\n') + '...'
  }

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm')
    } catch {
      return 'Unknown date'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-neon-purple mx-auto mb-4" />
          <p className="text-muted-foreground">Loading event information...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <PageHeader icon={Calendar} title="Event Information" />
          <p className="text-muted-foreground mt-1">
            Rules, schedules, and important announcements
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={handleCreatePage}
            className="flex items-center space-x-2 bg-neon-purple/10 hover:bg-neon-purple/20 text-neon-purple border border-neon-purple/30"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            <span>Create Page</span>
          </Button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Broadcast-notificatie — realtime toast + bell-item bij iedereen.
          Aparte flow van een aankondiging-pagina (die staat blijvend op de
          home onder Nieuws). Broadcast is éénmalig alarm. */}
      {isAdmin && <BroadcastCard />}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 bg-card border border-neon">
          <TabsTrigger value="all" className="data-[state=active]:bg-neon-purple/10 data-[state=active]:text-neon-purple">
            All
          </TabsTrigger>
          <TabsTrigger value="info" className="data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-500">
            Info
          </TabsTrigger>
          <TabsTrigger value="rules" className="data-[state=active]:bg-pink-500/10 data-[state=active]:text-pink-500">
            Rules
          </TabsTrigger>
          <TabsTrigger value="schedule" className="data-[state=active]:bg-green-500/10 data-[state=active]:text-green-500">
            Schedule
          </TabsTrigger>
          <TabsTrigger value="announcement" className="data-[state=active]:bg-purple-500/10 data-[state=active]:text-purple-500">
            Announcements
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredPages.length === 0 ? (
            <Card className="border-neon bg-card/50">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-center">
                  No {activeTab === 'all' ? 'event pages' : activeTab} found
                </p>
                {isAdmin && (
                  <Button
                    onClick={handleCreatePage}
                    className="mt-4"
                    variant="outline"
                  >
                    Create First Page
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {filteredPages.map((page) => (
                <Card
                  key={page.id}
                  className="group border-neon-purple/30 bg-card hover:shadow-neon-purple transition-all cursor-pointer"
                  onClick={() => handleViewPage(page)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getPageTypeBadgeColor(page.page_type)}>
                            {page.page_type}
                          </Badge>
                          {page.display_order > 0 && (
                            <Badge variant="outline" className="text-xs">
                              Order: {page.display_order}
                            </Badge>
                          )}
                          {page.is_published === 0 && (
                            <Badge variant="outline" className="text-xs text-yellow-500 border-yellow-500/30">
                              Draft
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-xl group-hover:text-neon-purple transition-colors">
                          {page.title}
                        </CardTitle>
                        <CardDescription className="text-xs mt-2">
                          Created: {formatDate(page.created_at)}
                          {page.updated_at && page.updated_at !== page.created_at && (
                            <> • Updated: {formatDate(page.updated_at)}</>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-muted-foreground whitespace-pre-line line-clamp-3 mb-4">
                      {truncateContent(page.content, 3)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleViewPage(page)
                        }}
                        className="flex-1"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Read More
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditPage(page)
                            }}
                            className="text-blue-500 hover:text-blue-600"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeletePage(page.id)
                            }}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto border-neon-purple/30">
          {selectedPage && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={getPageTypeBadgeColor(selectedPage.page_type)}>
                    {selectedPage.page_type}
                  </Badge>
                  {selectedPage.display_order > 0 && (
                    <Badge variant="outline" className="text-xs">
                      Order: {selectedPage.display_order}
                    </Badge>
                  )}
                </div>
                <DialogTitle className="text-2xl">{selectedPage.title}</DialogTitle>
                <DialogDescription className="text-xs">
                  Created: {formatDate(selectedPage.created_at)}
                  {selectedPage.updated_at && selectedPage.updated_at !== selectedPage.created_at && (
                    <> • Updated: {formatDate(selectedPage.updated_at)}</>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 whitespace-pre-line text-sm leading-relaxed">
                {selectedPage.content}
              </div>
              {isAdmin && (
                <DialogFooter className="mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setViewDialogOpen(false)
                      handleEditPage(selectedPage)
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setViewDialogOpen(false)
                      handleDeletePage(selectedPage.id)
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-neon-purple/30">
          <DialogHeader>
            <DialogTitle>{selectedPage ? 'Edit Page' : 'Create New Page'}</DialogTitle>
            <DialogDescription>
              {selectedPage ? 'Update the event page information' : 'Add a new event information page'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter page title"
                className="border-neon"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="page_type">Page Type</Label>
              <Select
                value={formData.page_type}
                onValueChange={(value) => setFormData({ ...formData, page_type: value })}
              >
                <SelectTrigger id="page_type" className="border-neon">
                  <SelectValue placeholder="Select page type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="rules">Rules</SelectItem>
                  <SelectItem value="schedule">Schedule</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Enter page content (line breaks will be preserved)"
                className="min-h-[300px] border-neon font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                placeholder="0"
                className="border-neon"
              />
              <p className="text-xs text-muted-foreground">
                Lower numbers appear first. Use 0 for default ordering.
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_published"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
              <Label htmlFor="is_published" className="cursor-pointer">
                Published (visible to all users)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePage}
              className="bg-neon-purple/10 hover:bg-neon-purple/20 text-neon-purple border border-neon-purple/30"
              disabled={!formData.title || !formData.content}
            >
              {selectedPage ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
